/**
 * codec-matrix.js — full audio + video codec matrix for the current browser.
 *
 * Answers "can THIS browser decode codec X" from the best signal available, in
 * this order — measured 2026-08-29, see the table below:
 *
 *   1. WebCodecs  `VideoDecoder/AudioDecoder.isConfigSupported`  ← asks the
 *      decoder that will actually run, so it knows about the MACHINE
 *      (hardware-only codecs, blocklisted drivers, decoder minimum sizes).
 *   2. `navigator.mediaCapabilities.decodingInfo`                ← knows some
 *      hardware limits, but shares the string APIs' blind spot in Firefox.
 *   3. `MediaSource.isTypeSupported` / `canPlayType`             ← last resort:
 *      these answer "does this BUILD know the codec name", not "can this
 *      machine decode it". Kept for the ~0.9 % of sessions without WebCodecs.
 *
 * ⚠️⚠️ Why the order is not cosmetic. Scored against ground truth (decode a real
 * 640x360 clip and read a painted frame back off a canvas), 16 cases = 4 codecs
 * x {real Chrome 152 + GPU, real Edge 152 + GPU, headless Chromium, Firefox}:
 *
 *   strategy                              correct  FALSE GREEN  cost/codec
 *   MSE || canPlayType (what we did)       15/16        1          0 ms
 *   mediaCapabilities.decodingInfo         15/16        1        6.9 ms
 *   WebCodecs isConfigSupported            16/16        0        0.0 ms
 *   (ground truth: real decode)              —          —         99 ms
 *
 * The single failing case is Firefox + HEVC: canPlayType says "probably",
 * MediaSource.isTypeSupported says true, mediaCapabilities says supported — and
 * the browser cannot decode HEVC at 64x64, 640x360 or 1280x720. Only WebCodecs
 * says no. The mirror-image case proves the same point from the other side:
 * real Chrome CAN decode HEVC at 640x360 but not at 64x64 (hardware decoder
 * minimum size), which the strings cannot express at all.
 *
 * ⚠️ Corollary for anyone measuring this again: a 64x64 fixture reports "Chrome
 * has no HEVC" and is WRONG. Probe at a realistic size (>=640x360) or the
 * fixture invents the defect. WebCodecs needs a SECURE CONTEXT — on a `data:`
 * URL the API is simply absent and every row silently falls back to strings.
 *
 * Each codec row:
 *   {
 *     family:   'video' | 'audio',
 *     id:       string,            // stable id (e.g. 'video.h264.high', 'audio.opus')
 *     label:    string,            // display name ('H.264 High Profile')
 *     mime:     string,            // MIME tested
 *     codec:    string,            // RFC-6381 codec id for WebCodecs
 *     mse:      'yes' | 'no',      // MediaSource.isTypeSupported
 *     element:  'probably' | 'maybe' | '' ,   // canPlayType
 *     decode:   'yes' | 'no' | null,          // best available decoder answer
 *     probe:    'webcodecs' | 'mediacapabilities' | 'strings',  // who answered
 *     summary:  'supported' | 'partial' | 'unsupported',
 *     consoles: { steamdeck, ps5, xbox, switch }   // expected support hint per console (for console-aware highlighting)
 *   }
 *
 * The `consoles.*` field is a curated *expected-support* hint — NOT a probe of
 * the current browser. It's used to highlight rows when ConsoleDetect.platform
 * matches a column. If we can't be confident, value is 'unknown'.
 *
 * `get()` stays SYNCHRONOUS and cheap for every existing caller: before
 * refinement it returns the string-derived rows exactly as it always did.
 * `ready()` returns a promise of the refined rows; after it resolves, `get()`
 * returns the refined set too. Callers that can await SHOULD — that is where
 * the accuracy lives. Guard: tests/playwright/codec-matrix-truth.mjs.
 */
(function(){
    'use strict';

    if (window.CodecMatrix && !window.CodecMatrix.__isStub) return;

    // ─── Curated codec list ───
    // Order matters for rendered output (most-asked first).
    var ROWS = [
        // Video
        {family:'video', id:'video.h264.baseline',  label:'H.264 Baseline',     mime:'video/mp4; codecs="avc1.42E01E"', codec:'avc1.42E01E', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'yes', xbox:'yes', switch:'yes'}},
        {family:'video', id:'video.h264.main',      label:'H.264 Main',         mime:'video/mp4; codecs="avc1.4D401E"', codec:'avc1.4D401E', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'yes', xbox:'yes', switch:'yes'}},
        {family:'video', id:'video.h264.high',      label:'H.264 High',         mime:'video/mp4; codecs="avc1.640028"', codec:'avc1.640028', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'yes', xbox:'yes', switch:'partial'}},
        {family:'video', id:'video.h265',           label:'H.265 / HEVC',       mime:'video/mp4; codecs="hev1.1.6.L93.B0"', codec:'hvc1.1.6.L93.B0', wcKnown:true,
         consoles:{steamdeck:'partial', ps5:'yes', xbox:'yes', switch:'no'}},
        {family:'video', id:'video.av1',            label:'AV1',                mime:'video/mp4; codecs="av01.0.05M.08"', codec:'av01.0.05M.08', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'yes', xbox:'partial', switch:'no'}},
        {family:'video', id:'video.vp9',            label:'VP9',                mime:'video/webm; codecs="vp09.00.10.08"', codec:'vp09.00.10.08', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'no', xbox:'yes', switch:'no'}},
        {family:'video', id:'video.vp8',            label:'VP8',                mime:'video/webm; codecs="vp8"', codec:'vp8', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'no', xbox:'yes', switch:'no'}},

        // Audio
        {family:'audio', id:'audio.aac',            label:'AAC (LC)',           mime:'audio/mp4; codecs="mp4a.40.2"', codec:'mp4a.40.2', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'yes', xbox:'yes', switch:'yes'}},
        {family:'audio', id:'audio.aac.he',         label:'AAC (HE)',           mime:'audio/mp4; codecs="mp4a.40.5"', codec:'mp4a.40.5', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'yes', xbox:'yes', switch:'partial'}},
        {family:'audio', id:'audio.mp3',            label:'MP3',                mime:'audio/mpeg', codec:'mp3', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'yes', xbox:'yes', switch:'yes'}},
        {family:'audio', id:'audio.opus',           label:'Opus',               mime:'audio/webm; codecs="opus"', codec:'opus', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'partial', xbox:'yes', switch:'no'}},
        {family:'audio', id:'audio.vorbis',         label:'Vorbis',             mime:'audio/webm; codecs="vorbis"', codec:'vorbis', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'no', xbox:'yes', switch:'no'}},
        {family:'audio', id:'audio.flac',           label:'FLAC',               mime:'audio/flac', codec:'flac', wcKnown:true,
         consoles:{steamdeck:'yes', ps5:'partial', xbox:'yes', switch:'no'}},
        {family:'audio', id:'audio.alac',           label:'ALAC',               mime:'audio/mp4; codecs="alac"', codec:'alac', wcKnown:false,
         consoles:{steamdeck:'no', ps5:'no', xbox:'no', switch:'no'}},
        {family:'audio', id:'audio.ac3',            label:'AC-3 (Dolby Digital)', mime:'audio/mp4; codecs="ac-3"', codec:'ac-3', wcKnown:false,
         consoles:{steamdeck:'no', ps5:'yes', xbox:'yes', switch:'no'}},
        {family:'audio', id:'audio.eac3',           label:'E-AC-3 (Dolby Digital Plus)', mime:'audio/mp4; codecs="ec-3"', codec:'ec-3', wcKnown:false,
         consoles:{steamdeck:'no', ps5:'yes', xbox:'yes', switch:'no'}}
    ];

    function _probeMse(mime) {
        try {
            return (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(mime)) ? 'yes' : 'no';
        } catch (_) { return 'no'; }
    }

    function _probeElement(family, mime) {
        try {
            var el = document.createElement(family);
            return el.canPlayType ? (el.canPlayType(mime) || '') : '';
        } catch (_) { return ''; }
    }

    function _summarize(mse, el) {
        if (mse === 'yes' || el === 'probably') return 'supported';
        if (el === 'maybe') return 'partial';
        return 'unsupported';
    }

    // ── Decoder probe (WebCodecs) ────────────────────────────────────────────
    // Resolves 'yes' / 'no' / null. null means "no usable answer" — the caller
    // falls through to the next signal. Two measured traps live here:
    //
    //  1. An UNRECOGNISED codec id resolves {supported:false}, not a rejection.
    //     Measured with a deliberate nonsense id ('zzz.9.9') on Chrome, Edge and
    //     Firefox: all three returned supported:false. So a bare `false` cannot
    //     be read as "this build knows the codec and cannot decode it".
    //  2. Chromium THROWS TypeError for `flac` and `vorbis` — codecs it plays
    //     perfectly well through <audio>. A throw therefore means "ask someone
    //     else", never "unsupported".
    //
    //  3. The registry is NARROWER than the media element. ALAC, AC-3 and E-AC-3
    //     are not WebCodecs codecs at all, so they resolve `supported:false`
    //     everywhere — including Safari, which plays all three through <audio>.
    //     Believing that `false` would trade the HEVC false green for a fresh
    //     false red on ~9 % of RU traffic. Those rows carry wcKnown:false and a
    //     WebCodecs `no` is ignored for them.
    //
    // Traps 1 and 3 are why a `no` may only contradict the string APIs for a
    // registered codec AND when the liveness control in _refine passes.
    function _probeDecoder(row) {
        // Unregistered codec: WebCodecs has nothing to say, and what it says
        // ('supported:false') is indistinguishable from a real refusal. Skip it
        // rather than spend a call producing an answer we must then ignore.
        if (row.wcKnown === false) return Promise.resolve(null);
        var Ctor = row.family === 'video'
            ? (typeof VideoDecoder !== 'undefined' ? VideoDecoder : null)
            : (typeof AudioDecoder !== 'undefined' ? AudioDecoder : null);
        if (!Ctor || !Ctor.isConfigSupported) return Promise.resolve(null);
        // 640x360 is deliberate: hardware decoders have MINIMUM dimensions, and a
        // 64x64 probe reports "real Chrome has no HEVC" — measured, and wrong.
        var cfg = row.family === 'video'
            ? { codec: row.codec, codedWidth: 640, codedHeight: 360, hardwareAcceleration: 'no-preference' }
            : { codec: row.codec, sampleRate: 48000, numberOfChannels: 2 };
        try {
            return Ctor.isConfigSupported(cfg).then(
                function (r) { return (r && r.supported) ? 'yes' : 'no'; },
                function () { return null; }   // trap 2
            );
        } catch (_) { return Promise.resolve(null); }
    }

    // mediaCapabilities — knows some hardware limits the strings cannot express
    // (it is the signal that correctly reported "no HEVC at 64x64" on real
    // Chrome), but shares their blind spot in Firefox. Second choice, not first.
    function _probeMediaCapabilities(row) {
        var mc = navigator.mediaCapabilities;
        if (!mc || !mc.decodingInfo) return Promise.resolve(null);
        var cfg = { type: 'file' };
        cfg[row.family] = row.family === 'video'
            ? { contentType: row.mime, width: 640, height: 360, framerate: 30, bitrate: 1000000 }
            : { contentType: row.mime, channels: '2', samplerate: 48000, bitrate: 128000 };
        try {
            return mc.decodingInfo(cfg).then(
                function (i) { return (i && i.supported) ? 'yes' : 'no'; },
                function () { return null; }
            );
        } catch (_) { return Promise.resolve(null); }
    }

    var _result = null;
    function _build() {
        return Object.freeze(ROWS.map(function(row) {
            var mse = _probeMse(row.mime);
            var el  = _probeElement(row.family, row.mime);
            return Object.freeze({
                family:   row.family,
                id:       row.id,
                label:    row.label,
                mime:     row.mime,
                codec:    row.codec,
                wcKnown:  row.wcKnown !== false,
                mse:      mse,
                element:  el,
                decode:   null,
                probe:    'strings',
                summary:  _summarize(mse, el),
                consoles: Object.freeze(Object.assign({}, row.consoles))
            });
        }));
    }

    // ── Async refinement ─────────────────────────────────────────────────────
    // Replaces `summary` with a decoder-backed verdict where one is available.
    // Idempotent and memoised; `get()` serves the refined rows afterwards.
    var _refined = null;
    function _refine() {
        var base = _result || (_result = _build());
        return Promise.all(base.map(function (row) {
            return _probeDecoder(row).then(function (wc) {
                if (wc !== null) return { row: row, verdict: wc, probe: 'webcodecs' };
                return _probeMediaCapabilities(row).then(function (mc) {
                    return { row: row, verdict: mc, probe: mc === null ? 'strings' : 'mediacapabilities' };
                });
            });
        })).then(function (answers) {
            // Liveness control for trap 1. A WebCodecs `no` may only overrule a
            // positive string when the same API said `yes` to something in the
            // same family — that proves the registry is live on this build, so a
            // `no` carries information. If WebCodecs refused everything it is far
            // likelier to be unavailable than for the machine to lack every
            // codec, and we keep the old behaviour rather than invent false reds.
            var live = {};
            answers.forEach(function (a) {
                if (a.probe === 'webcodecs' && a.verdict === 'yes') live[a.row.family] = true;
            });
            return Object.freeze(answers.map(function (a) {
                var row = a.row, v = a.verdict, probe = a.probe;
                var mayOverrule = (probe === 'mediacapabilities') ||
                                  (probe === 'webcodecs' && (v === 'yes' || (live[row.family] && row.wcKnown)));
                if (v === null || !mayOverrule) {
                    return Object.freeze(Object.assign({}, row, { decode: v, probe: 'strings' }));
                }
                return Object.freeze(Object.assign({}, row, {
                    decode:  v,
                    probe:   probe,
                    summary: v === 'yes' ? 'supported'
                           : (row.element === 'maybe' ? 'partial' : 'unsupported')
                }));
            }));
        }).catch(function () { return base; });
    }

    var _readyPromise = null;

    var CodecMatrix = {
        // Synchronous, cheap, unchanged for every existing caller. Returns the
        // refined rows once ready() has resolved.
        get: function() {
            if (_refined) return _refined;
            if (!_result) _result = _build();
            return _result;
        },
        // Promise of the decoder-backed rows. Await this wherever the answer
        // decides something the user will see or feel.
        ready: function() {
            if (!_readyPromise) {
                _readyPromise = _refine().then(function (rows) { _refined = rows; return rows; });
            }
            return _readyPromise;
        },
        // Convenience for the common question: "will <video>/<audio> decode this
        // here?" Sync — reflects the best answer known so far.
        canDecode: function(id) {
            var rows = CodecMatrix.get();
            for (var i = 0; i < rows.length; i++) {
                if (rows[i].id === id) return rows[i].summary === 'supported';
            }
            return false;
        },
        // For Playwright tests
        __forceForTest: function(rows) { _result = Object.freeze(rows); _refined = _result; }
    };

    Object.defineProperty(CodecMatrix, '__isStub', { value: false });
    window.CodecMatrix = CodecMatrix;
})();
